'use client'
import { useEffect, useState } from 'react'
import { usePushPartial } from './_hooks/use-push-partial'
import { CaptureNameDialog } from './_components/CaptureNameDialog'
import { CaptureEmailDialog } from './_components/CaptureEmailDialog'
import { CapturePhoneDialog } from './_components/CapturePhoneDialog'
import { CreatingOrgDialog } from './_components/CreatingOrgDialog'
import { PageCreatedDialog } from './_components/OrgCreatedDialog'
import { CaptureCodeDialog } from './_components/CaptureCodeDialog'
import { ErrorDialog } from './_components/ErrorDialog'
import { AnimatePresence, motion } from 'framer-motion'
import { useRouter, useSearchParams } from 'next/navigation'

export default function ClientPage() {
  const { pushPartial } = usePushPartial()
  const [orgName, setOrgName] = useState('')
  const [remainingTime, setRemainingTime] = useState(0)
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')

  const query = useSearchParams()
  const router = useRouter()

  const step = orgName ? query.get('step') || 'name' : 'name'
  const [stepHistory, setStepHistory] = useState([step])

  useEffect(() => {
    if (stepHistory[stepHistory.length - 1] !== step) {
      setStepHistory([...stepHistory, step])
    }
  }, [step])

  if (!step) {
    return null
  }

  const variants = {
    exit: { opacity: 0 },
  }

  return (
    <>
      <AnimatePresence mode="wait" initial={true}>
        {step === 'name' && (
          <motion.div
            key="name-dialog"
            variants={variants}
            exit="exit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ easeIn: 'linear' }}
          >
            <CaptureNameDialog
              defaultValue={orgName}
              onNext={(newName) => {
                setOrgName(newName)
                pushPartial({ orgName: newName })
                router.push('/start?step=email')
              }}
            />
          </motion.div>
        )}
        {step === 'email' && (
          <motion.div
            key="email-dialog"
            variants={variants}
            exit="exit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ easeIn: 'linear' }}
          >
            <CaptureEmailDialog
              defaultValue={email}
              onNext={(newEmail) => {
                setEmail(newEmail)
                pushPartial({ orgName, email: newEmail })
                router.push('/start?step=phone')
              }}
              onBack={() => {
                router.back()
              }}
            />
          </motion.div>
        )}
        {step === 'phone' && (
          <motion.div
            key="phone-dialog"
            variants={variants}
            exit="exit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ easeIn: 'linear' }}
          >
            <CapturePhoneDialog
              defaultValue={phoneNumber}
              onNext={(newPhoneNumber, retryInSeconds) => {
                setRemainingTime(retryInSeconds)
                setPhoneNumber(newPhoneNumber)
                pushPartial({ orgName, email, phoneNumber: newPhoneNumber })
                router.push('/start?step=code')
              }}
              onBack={() => {
                router.back()
              }}
              email={email}
            />
          </motion.div>
        )}
        {step === 'code' && (
          <motion.div
            key="code-dialog"
            variants={variants}
            exit="exit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ easeIn: 'linear' }}
          >
            <CaptureCodeDialog
              defaultRemainingTime={remainingTime}
              orgName={orgName}
              email={email}
              phoneNumber={phoneNumber}
              onNext={() => {
                router.push('/start?step=creation')
              }}
              onBack={() => {
                router.back()
              }}
            />
          </motion.div>
        )}

        {step === 'creation' && (
          <motion.div
            key="creation-dialog"
            variants={variants}
            exit="exit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ easeIn: 'linear' }}
          >
            <CreatingOrgDialog
              onComplete={() => {
                router.replace('/start?step=created')
              }}
            />
          </motion.div>
        )}
        {step === 'created' && (
          <motion.div
            key="created-dialog"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ easeIn: 'linear', duration: 0.5 }}
          >
            <PageCreatedDialog name={orgName} />
          </motion.div>
        )}
        {step === 'error' && (
          <motion.div
            key="error-dialog"
            variants={variants}
            exit="exit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ easeIn: 'linear' }}
          >
            <ErrorDialog
              onRestart={() => {
                router.replace('/start?step=name')
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
